import {ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
  ActivityFeedInfo, ActivityFeedType,
  ConversationsByFilter, DefaultActivityFilterSettings, ReferenceNotificationPreferenceType, ReferenceNotificationSettings
} from '@features/task-manager/collaboration/store/activity.model';
import {UserModel} from "@shared/models/user.model";
import {Store} from "@ngrx/store";
import {SecurityState} from "@shared/store/reducers/security.reducer";
import {getUserInfo} from "@shared/store/selectors/security.selector";
import {Subject} from 'rxjs';
import {UserInfo} from '@shared/models/user-info.model';
import {ActivityFilterService} from '@features/task-manager/collaboration/services/activity-filter.service';
import {skip, take, takeUntil} from 'rxjs/operators';
import {getCurrentPlatformLink, ParticleState} from '@shared/store/reducers/particle.reduer';
import {MagicLinkModel} from '@shared/models/platform-tool-models';
import {PlatformQueryService} from '@core/services/platform-query.service';
import {PlatformQuery} from '@shared/models/platform-query/platform-query';
import {Api} from '@shared/constants/common-field.constants';
import {BehaviorSubject} from 'rxjs';
import {DialogModel} from '@shared/models/dialog.model';
import {ActivityDateRangeTypeScope} from '@features/task-manager/collaboration/store/activity-date-range-type-scope.model';
import {DialogService} from '@shared/services/dialog-service';
import {ActivityFilterSettings} from '@shared/models/activity-tracker-header.model';
import moment_ from 'moment';
import {SnackBarService} from '@core/services/snack-bar.service';
import {PlatformCommand} from '@shared/commands/command-base';
import {CommandService} from '@core/services/command.service';

@Component({
    standalone: false,
    selector: 'particle-activity-cards',
  templateUrl: './activity-cards.component.html',
  styleUrl: './activity-cards.component.scss'
})
export class ActivityCardsComponent implements OnInit, OnDestroy {
  @Output() activityFeedInfoEvent$: EventEmitter<any> = new EventEmitter<any>();
  @Input() activitiesRootEvent$: BehaviorSubject<any> = new BehaviorSubject(null);
  @Input() public data: ActivityFeedInfo;
  @Input() public showChatEditor = false;
  // HS-E-21-12: the panel menu's "Support activities only", so cards created again start in step with it.
  @Input() public supportOnly = false;

  @Input() public activityFeeds: ActivityFeedInfo[] = [];
  changeNotificationSettingsEvent$: BehaviorSubject<ReferenceNotificationSettings> = new BehaviorSubject(null);
  platformLink: MagicLinkModel = null;
  private _feedInfo: ActivityFeedInfo = null;
  public get feedInfo(): ActivityFeedInfo {
    return this._feedInfo;
  }
  @Input() public set feedInfo(value: ActivityFeedInfo) {
    this._feedInfo = value;
    this.activityFeedInfoEvent$.emit({
      type: 'ActivityFeedInfoChanged',
      data: this._feedInfo
    });
  }
  allContacts: Array<any> = [];
  hashTagList: Array<string> = [];
  public unsubscribe$ = new Subject<void>();
  conversationsByFilter: ConversationsByFilter = null;
  userInfo$: any;
  userInfo: UserInfo;
  currentUser: UserModel;
  ngUnsubscribe = new Subject<void>();
  validateFeedsInfo = false;
  expandedFeedInfo = '';
  fetchActivity = false;
  filterSettings: ActivityFilterSettings = JSON.parse(JSON.stringify(DefaultActivityFilterSettings));
  hasMultipleFeeds = false;

  constructor(private securityStore: Store<SecurityState>,
              private activityFilterService: ActivityFilterService,
              private particleStore: Store<ParticleState>,
              private platformQueryService: PlatformQueryService,
              private dialogService: DialogService,
              private snackBarService: SnackBarService,
              private commandService: CommandService,
              private changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    console.log('initiating activity feed:', this.data);
    this.filterSettings.supportOnly = this.supportOnly;
    if (this.activityFeeds.length > 0) {
      this.hasMultipleFeeds = true;
    }
    this.updateFeedInfo();
    this.userInfo$ = this.securityStore.select(getUserInfo);
    this.userInfo$.subscribe(userInfo => {
      this.userInfo = userInfo;
    });
    this.particleStore.select(getCurrentPlatformLink).pipe(takeUntil(this.unsubscribe$)).subscribe((platformLink: any) => {
        this.platformLink = platformLink;
      });
    this.activityFilterService.activityFeeds$.pipe(takeUntil(this.unsubscribe$)).subscribe((value: boolean) => {
      this.fetchActivity = value;
      this.validateFeedsInfo = false;
      if (this.fetchActivity) {
        this.getActivityFeeds();
      } else {
        this.handleShowChatAndExpandInfo();
      }
    });
    this.activitiesRootEvent$.pipe(skip(1), takeUntil(this.ngUnsubscribe)).subscribe(value => {
        if (value) {
          this.handleActivitiesRootEvent(value);
        }
      });
  }
  updateFeedInfo() {
    if (this.hasMultipleFeeds === false && this.data) {
      this.activityFeeds = [this.data];
    }
    if (this.data && this.data?.type) {
      this.feedInfo = (JSON.parse(JSON.stringify(this.data)));
    } else if (this.activityFeeds.length > 0 && this.activityFeeds[0]?.type) {
      this.feedInfo = (JSON.parse(JSON.stringify(this.activityFeeds[0])));
    }
  }
  handleActivitiesRootEvent(event: any) {
    switch (event?.type) {
      case 'Refresh':
        this.handleRefreshEvent(event);
        break;
      case 'DateRange':
        this.handleChangeDateEvent(event);
        break;
      case 'SupportOnly':
        this.handleSupportOnlyEvent(event);
        break;
      case 'Export':
        this.handleExportAllActivityEvent(event);
        break;
      case 'Notifications':
        this.handleChangeNotificationsEvent(event);
        break;
      default:
        break;
    }
  }
  handleRefreshEvent(event: any) {
    this.validateFeedsInfo = false;
    this.getActivityFeeds();
  }
  // HS-E-21-12: reload the feeds with or without the support-only condition, the way a new date range reloads them.
  handleSupportOnlyEvent(event: any) {
    this.filterSettings.supportOnly = !!event.value;
    this.validateFeedsInfo = false;
    this.getActivityFeeds();
  }
  handleChangeDateEvent(event: any) {
    const dialogModel: DialogModel = DialogModel.create(
      'Date Range',
      'DateRangeSelector',
      {dateRangeType: this.filterSettings.dateRangeType, startDate: this.filterSettings.startDate, endDate: this.filterSettings.endDate},
      '800px',
      '800px'
    );
    this.dialogService.showOkCancelDialog(dialogModel).subscribe(res => {
      if (res.action === 'OK') {
        const data = res.data.input;
        if (this.feedInfo?.id) {
          this.filterSettings.dateRangeType = data.dateRangeType;
          this.filterSettings.startDate = data.startDate;
          this.filterSettings.endDate = data.endDate;
          this.validateFeedsInfo = false;
          this.getActivityFeeds();
        }
      }
    });
  }
  handleExportAllActivityEvent(event: any) {
    const feeds = JSON.parse(JSON.stringify(this.activityFeeds));
    if (feeds && feeds?.length > 0) {
      for (let i = 0; i < feeds?.length; i++) {
        feeds[i].auditTrail = this.filterSettings.auditTrail;
        feeds[i].dateRangeType = this.filterSettings.dateRangeType;
        feeds[i].startDate = this.getStartDate(this.filterSettings.startDate);
        feeds[i].endDate = this.getEndDate(this.filterSettings.endDate);
        feeds[i].relatedToolIdForFilter = '';
        feeds[i].supportOnly = !!this.filterSettings.supportOnly;
      }
      this.activityFilterService.exportActivityFeeds(feeds, 'AllExcel');
    }
  }
  handleChangeNotificationsEvent(event: any) {
    if (!this.feedInfo || !this.feedInfo.type || !this.feedInfo.id || this.feedInfo.type.toLowerCase() !== ActivityFeedType.ReferenceCode.toLowerCase()) {
      this.snackBarService.showError('Notifications does not apply in current activity feed.');
      return;
    }
    const query = new PlatformQuery();
    query.name = 'ReferenceNotificationSettingsQuery';
    query.api = 'CollaborationApi';
    query.fieldValues['ReferenceCode'] = this.activityFeeds[0]?.id;
    query.fieldValues['Title'] = this.feedInfo.title;
    let defaultNotificationPreference = ReferenceNotificationPreferenceType.MyMentions;
    switch (this.feedInfo.sourceKey) {
      case 'ExpertService':
        defaultNotificationPreference = ReferenceNotificationPreferenceType.NotifyAllMessages;
        break;
    }
    query.fieldValues['DefaultNotificationPreference'] = defaultNotificationPreference;
    query.impersonationId = this.getImpersonationId(this.feedInfo.isUseImpersonation, this.feedInfo.impersonationId);
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response => {
      if (response && response.fieldValues && response.fieldValues['ReferenceNotificationSettingsModel']) {
        const referenceNotificationSettingsModel = response.fieldValues['ReferenceNotificationSettingsModel'];
        const dialogModel: DialogModel = DialogModel.create(
          this.feedInfo.title,
          'ReferenceNotificationSettingsComponent',
          {referenceNotificationSettings: referenceNotificationSettingsModel},
          '800px',
          '800px'
        );
        this.dialogService.showOkCancelDialog(dialogModel).subscribe(res => {
          if (res.action === 'OK') {
            console.log('notification settings: ', res.data.input);
            const data = res.data.input;
            const notificationSettings = data.referenceNotificationSettings;
            this.changeNotificationSettingsEvent$.next(notificationSettings);
          }
        });
      } else {
        this.snackBarService.showError('Model fetching failed!');
      }
    }, err => {
      this.snackBarService.showError('Model fetching failed!');
    });
  }
  getActivityFeeds() {
    const query = new PlatformQuery();
    query.api = Api.Spreadsheet;
    query.name = 'GetStepByStepActivityFeedsInfoQuery';
    query.fieldValues['ScenarioCalculationId'] = this.platformLink?.fieldValues['ScenarioCalculationId'];
    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe((res: any) => {
      this.activityFeeds = res.fieldValues['ActivityFeedsInfo'] ? res.fieldValues['ActivityFeedsInfo'] : this.activityFeeds;
      this.handleShowChatAndExpandInfo();
      this.changeDetectorRef.detectChanges();
    }, err => {
      console.error(err);
    });
  }
  handleShowChatAndExpandInfo() {
    if (this.activityFeeds?.length > 0) {
      const len = this.activityFeeds.length;
      if (this.showChatEditor) {
        this.activityFeeds[len - 1].showChatEditor = true;
      }
      this.expandedFeedInfo = this.activityFeeds[len - 1].id;
    }
    this.validateFeedsInfo = true;
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
  handleActivityFeedInfoEvent(event: any): void {
    switch (event?.type) {
      case 'ChatEditorStateChanged':
        this.activityFeedInfoEvent$.emit(event);
        break;
      case 'ActivityFeedInfoChanged':
        this.handleActivityFeedInfoChanged(event);
        break;
      default:
        break;
    }
  }
  handleActivityFeedInfoChanged(event: any): void {
    if (this.hasMultipleFeeds) {
      return;
    }
    if ((!this.data || this.data.id !== event.data.id)) {
      this.data = event.data;
      this.updateFeedInfo();
    } else if (this.activityFeeds.length === 0) {
      this.activityFeeds = [this.data];
    }
  }
  handleExpandedInfoEvent(event: any): void {
    switch (event?.type) {
      case 'open':
        this.onOpen(event?.feedId);
        break;
      case 'close':
        this.onClose(event?.feedId);
        break;
    }
  }
  onClose(feedId: string) {
  }
  onOpen(feedId: string) {
    this.expandedFeedInfo = feedId;
    this.changeDetectorRef.detectChanges();
  }
  private getStartDate(dateTime: any) {
    const startDate = new Date(dateTime);
    const sDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0);
    const moment = moment_;
    return moment.utc(sDate).toDate();
  }

  private getEndDate(dateTime: any) {
    const endDate = new Date(dateTime);
    const eDate = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59);
    const moment = moment_;
    return moment.utc(eDate).toDate();
  }
  private getImpersonationId(isUseImpersonation: boolean, impersonationId: string) {
    if (isUseImpersonation) {
      return impersonationId;
    }
    return '';
  }
}
